import { trackCotacaoStepView } from "@/analytics/tracking";
import QuoteFlow from "@/components/pages/quoteFlow";
import type { CarouselApi } from "@/components/ui/carousel";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { GoogleIcon } from "@/components/ui/google-icon";
import ImageSvg from "@/components/ui/image-svg";
import Autoplay from "embla-carousel-autoplay";
import { Clipboard, MapPin, UserCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { FaCarAlt } from "react-icons/fa";

export default function Quote() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    trackCotacaoStepView("inicio");
  }, []);

  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  return (
    <div className="min-h-screen container md:py-20 py-10">
      <div className="flex flex-col md:flex-row md:justify-between gap-4 md:gap-8">
        <div className="lg:hidden">
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            plugins={[
              Autoplay({
                delay: 4000,
              }),
            ]}
            className="w-full"
          >
            <CarouselContent className="-ml-2">
              <CarouselItem className="pl-2 basis-[85%]">
                <div className="bg-white rounded-full shadow-sm px-3 py-2 flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-n-bg-1 p-1 flex items-center justify-center flex-shrink-0">
                    <ImageSvg
                      src="reclameaqui.svg"
                      alt="Reclame Aqui"
                      className="object-contain w-full h-full"
                    />
                  </div>
                  <p className="font-medium text-xs text-n-text-green whitespace-nowrap">
                    Somos nota máxima no Reclame Aqui!
                  </p>
                </div>
              </CarouselItem>

              <CarouselItem className="pl-2 basis-[68%]">
                <div className="bg-white rounded-full shadow-sm px-3 py-2 flex items-center gap-3">
                  <div className="w-5 h-5 flex-shrink-0">
                    <GoogleIcon />
                  </div>
                  <p className="font-medium text-xs text-n-text-green whitespace-nowrap">
                    5 Estrelas no Google Review
                  </p>
                </div>
              </CarouselItem>

              <CarouselItem className="pl-2 basis-[80%]">
                <div className="bg-white rounded-full shadow-sm px-3 py-2 flex items-center gap-3">
                  <FaCarAlt className="w-5 h-5 flex-shrink-0 text-gray-700" />
                  <p className="font-medium text-xs text-n-text-green whitespace-nowrap">
                    + de 200.000 veículos transportados
                  </p>
                </div>
              </CarouselItem>
            </CarouselContent>
          </Carousel>
        </div>
        <div className="hidden lg:block w-[50%]">
          <h1 className="text-5xl font-semibold text-n-text-green m-6">
            Calcular o seu transporte é fácil e rapido!
          </h1>

          <Carousel
            setApi={setApi}
            plugins={[
              Autoplay({
                delay: 5000,
              }),
            ]}
            className="w-full"
          >
            <CarouselContent>
              <CarouselItem>
                <div className="flex flex-col items-center justify-center h-[500px]">
                  <ImageSvg
                    src={"mulher-cotacao.svg"}
                    alt="Quote illustration"
                    className="h-[400px]"
                  />
                </div>
              </CarouselItem>

              <CarouselItem>
                <div className="flex flex-col gap-4 p-6 h-[500px] justify-center items-center">
                  <div className="bg-white rounded-2xl shadow-md p-6 flex flex-col items-start gap-4 w-full max-w-[400px]">
                    <div className="w-12 h-12 rounded-full bg-n-bg-1 p-3 flex items-center justify-center">
                      <ImageSvg
                        src="reclameaqui.svg"
                        alt="Reclame Aqui"
                        className="object-contain w-full h-full"
                      />
                    </div>
                    <p className="font-semibold text-base">
                      Somos nota máxima no Reclame Aqui!
                    </p>
                  </div>

                  <div className="bg-white rounded-2xl shadow-md p-6 flex flex-col items-start gap-4 w-full max-w-[400px]">
                    <div className="w-12 h-12 flex-shrink-0">
                      <GoogleIcon />
                    </div>
                    <p className="font-semibold text-base">
                      5 Estrelas no Google Review
                    </p>
                  </div>

                  <div className="bg-white rounded-2xl shadow-md p-6 flex flex-col items-start gap-4 w-full max-w-[400px]">
                    <FaCarAlt className="w-12 h-12 flex-shrink-0 text-gray-700" />
                    <p className="font-semibold text-base">
                      + de 200.000 veículos transportados
                    </p>
                  </div>
                </div>
              </CarouselItem>

              <CarouselItem>
                <div className="flex flex-col gap-4 p-6 h-[500px] justify-center items-center">
                  <div className="bg-white rounded-2xl shadow-md p-6 flex flex-col items-start gap-4 w-full max-w-[400px]">
                    <UserCheck className="w-12 h-12 flex-shrink-0 text-gray-700" />
                    <p className="font-semibold text-base">
                      + de 750.000 clientes atendidos
                    </p>
                  </div>

                  <div className="bg-white rounded-2xl shadow-md p-6 flex flex-col items-start gap-4 w-full max-w-[400px]">
                    <Clipboard className="w-12 h-12 flex-shrink-0 text-gray-700" />
                    <p className="font-semibold text-base">
                      + de 200.000 veículos transportados
                    </p>
                  </div>

                  <div className="bg-white rounded-2xl shadow-md p-6 flex flex-col items-start gap-4 w-full max-w-[400px]">
                    <MapPin className="w-12 h-12 flex-shrink-0 text-gray-700" />
                    <p className="font-semibold text-base">
                      + de 200 bases pelo Brasil
                    </p>
                  </div>
                </div>
              </CarouselItem>
            </CarouselContent>
          </Carousel>

          <div className="flex justify-center gap-2 mt-4">
            {[0, 1, 2].map((index) => (
              <button
                key={index}
                onClick={() => api?.scrollTo(index)}
                className={`h-2 rounded-full transition-all ${
                  current === index
                    ? "bg-green-700 w-8"
                    : "bg-gray-300 w-2 hover:bg-gray-400"
                }`}
                aria-label={`Ir para slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        <QuoteFlow className="md:w-[600px]" />
      </div>
    </div>
  );
}
